import _ from "lodash";
import BaseDecorator from "./BaseDecorator";
import * as MoneySum from "lib/utils/MoneySum";

export default class OrderDecorator extends BaseDecorator {
  static pricingPlanTypes = ["elastic", "not_refundable"];
  static productTypes = ["accommodation", "coupon", "standard", "ticket", "voucher"];
  static statuses = [
    "building",
    "waiting_for_customer_approval",
    "waiting_for_company_approval",
    "approved",
    "finished",
    "cancelled",
    "rejected",
    "abandoned",
  ];
  static active_statuses = [
    "building",
    "waiting_for_company_approval",
    "waiting_for_customer_approval",
    "approved",
    "finished",
  ];

  static getPricingPlanTypeLabel(pricingPlanType) {
    return I18n.t(`activerecord.attributes.pricing_plan_types.${pricingPlanType}`);
  }

  static getProductTypeLabel(productType) {
    return I18n.t(`activerecord.attributes.product_types.${productType}`);
  }

  static getStatusLabel(status, shortLabel = false) {
    return (
      (shortLabel &&
        I18n.t(`activerecord.attributes.order.status_short.${status}`, {
          defaultValue: "",
        })) ||
      I18n.humanAttributeName("Order", status)
    );
  }

  static get balance_status_options() {
    return [
      "unpaid",
      "partly_paid",
      "fully_paid",
      "overpaid",
      "partly_refunded",
      "fully_refunded",
      "over_refunded",
    ].map((v) => [v, I18n.humanAttributeName("order", v)]);
  }

  static get status_options() {
    return this.statuses.map((st) => [st, I18n.humanAttributeName("order", st)]);
  }
  static get new_order_status_options() {
    return ["building", "waiting_for_company_approval", "waiting_for_customer_approval", "approved", "finished"].map(
      (st) => [st, I18n.humanAttributeName("order", st)]
    );
  }
  static get existing_order_status_options() {
    return [
      "waiting_for_customer_approval",
      "waiting_for_company_approval",
      "approved",
      "finished",
      "cancelled",
      "rejected",
      "abandoned",
    ].map((st) => [st, I18n.humanAttributeName("order", st)]);
  }

  get withdrawn() {
    return ["cancelled", "abandoned", "rejected"].includes(this.status);
  }

  get company_id() {
    return this.company ? this.company.id : null;
  }
  get agent_id() {
    return this.agent ? this.agent.id : null;
  }
  get supplier() {
    return this.company;
  }
  get supplier_id() {
    return this.company_id;
  }

  get status_i18n() {
    return I18n.humanAttributeName("order", this.status);
  }

  get has_pricing() {
    return ["standard", "accommodation", "ticket", "voucher"].includes(this.product_type);
  }

  get product_full_name() {
    return _.compact([this.product_name, this.product_variant_name && `(${this.product_variant_name})`]).join(" ");
  }

  get balance_status_i18n() {
    return this.balance_status && I18n.humanAttributeName("order", this.balance_status);
  }

  get active() {
    return this.constructor.active_statuses.includes(this.status);
  }

  get author_place_formatted() {
    if (this.author_place === "frontend") {
      return I18n.humanAttributeName("order", "author_place_frontend");
    } else if (this.author_place === "customerapp") {
      return I18n.humanAttributeName("order", "author_place_cwa");
    }
    return I18n.humanAttributeName("order", "author_place_partner");
  }

  canCallAction(action) {
    switch (action) {
      case "cancel":
        return [
          "building",
          "waiting_for_customer_approval",
          "waiting_for_company_approval",
          "approved",
          "finished",
        ].includes(this.status);
      case "change_status":
        return true;
      case "redo":
        return this.manual && this.status === "building";
      case "approve":
      case "reject":
        return this.status === "waiting_for_company_approval";
      case "finish":
        return this.status === "approved";
      case "confirm":
        return this.status === "building";
      default:
        return true;
    }
  }

  get paid_amount_sum() {
    return this.customer_paid_amount_sum;
  }
  get refunded_amount_sum() {
    return this.customer_received_amount_sum;
  }
  get overpaid_total_amount_sum() {
    return this.customer_remaining_received_amount_sum;
  }
  get remaining_total_amount_sum() {
    return this.customer_remaining_paid_amount_sum;
  }

  get customer_earnings_sum() {
    return MoneySum.invert(this.total_amount_sum);
  }
  get customer_outcome_sum() {
    return MoneySum.subtract(this.customer_paid_amount_sum, this.customer_received_amount_sum);
  }
  get customer_real_outcome_sum() {
    return MoneySum.subtract(this.customer_paid_transaction_amount_sum, this.customer_received_transaction_amount_sum);
  }
  get customer_balance_sum() {
    return MoneySum.sum(this.customer_earnings_sum, this.customer_outcome_sum);
  }
  get customer_remaining_received_amount_sum() {
    return this.customer_balance_sum.filter((m) => m.value > 0);
  }
  get customer_remaining_paid_amount_sum() {
    if (this.withdrawn) {
      return [];
    } else {
      return this.customer_balance_sum.filter((m) => m.value < 0).map((m) => ({ ...m, value: -m.value }));
    }
  }

  get socialtravel_outcome_sum() {
    return MoneySum.subtract(this.socialtravel_paid_amount_sum, this.socialtravel_received_amount_sum);
  }
  get socialtravel_real_outcome_sum() {
    return MoneySum.subtract(
      this.socialtravel_paid_transaction_amount_sum,
      this.socialtravel_received_transaction_amount_sum
    );
  }
  get socialtravel_balance_sum() {
    return MoneySum.sum(this.socialtravel_earnings_sum, this.socialtravel_outcome_sum);
  }
  get socialtravel_remaining_received_amount_sum() {
    return this.socialtravel_balance_sum.filter((m) => m.value > 0);
  }
  get socialtravel_remaining_paid_amount_sum() {
    return this.socialtravel_balance_sum.filter((m) => m.value < 0).map((m) => ({ ...m, value: -m.value }));
  }

  get agent_outcome_sum() {
    return MoneySum.subtract(this.agent_paid_amount_sum, this.agent_received_amount_sum);
  }
  get agent_real_outcome_sum() {
    return MoneySum.subtract(this.agent_paid_transaction_amount_sum, this.agent_received_transaction_amount_sum);
  }
  get agent_balance_sum() {
    return MoneySum.sum(this.agent_earnings_sum, this.agent_outcome_sum);
  }
  get agent_remaining_received_amount_sum() {
    return this.agent_balance_sum.filter((m) => m.value > 0);
  }
  get agent_remaining_paid_amount_sum() {
    return this.agent_balance_sum.filter((m) => m.value < 0).map((m) => ({ ...m, value: -m.value }));
  }

  get supplier_outcome_sum() {
    return MoneySum.subtract(this.supplier_paid_amount_sum, this.supplier_received_amount_sum);
  }
  get supplier_real_outcome_sum() {
    return MoneySum.subtract(this.supplier_paid_transaction_amount_sum, this.supplier_received_transaction_amount_sum);
  }
  get supplier_balance_sum() {
    return MoneySum.sum(this.supplier_earnings_sum, this.supplier_outcome_sum);
  }
  get supplier_remaining_received_amount_sum() {
    return this.supplier_balance_sum.filter((m) => m.value > 0);
  }
  get supplier_remaining_paid_amount_sum() {
    return this.supplier_balance_sum.filter((m) => m.value < 0).map((m) => ({ ...m, value: -m.value }));
  }
}



// WEBPACK FOOTER //
// ./src/lib/decorators/OrderDecorator.js